import { Sorter } from '@flakiness/server/common/pagedResponse.js';
import { EnvGroup } from '@flakiness/server/common/stats/envGroup.js';
import { Stats } from '@flakiness/server/common/stats/stats.js';
import { wireOutcomesToOutcome, WireTypes } from '@flakiness/server/common/wireTypes.js';
import { CHECK } from '@flakiness/shared/common/utils.js';
import { ContextType } from '@lit/context';
import { Task } from '@lit/task';
import { SlAfterHideEvent, SlDialog } from '@shoelace-style/shoelace';
import { css, html, LitElement, nothing, TemplateResult } from 'lit';
import { customElement, property, query, state } from 'lit/decorators.js';
import { classMap } from 'lit/directives/class-map.js';
import { styleMap } from 'lit/directives/style-map.js';
import { api } from '../api.js';
import { linkStyles } from '../components/cssstyles.js';
import { consume, contexts } from '../contexts.js';
import { renderDate } from '../fragments.js';
import { hrefOrClick, Linkifier } from '../linkifier.js';
import { PageCommit } from '../page-commit.js';
import { PageRun } from '../page-run.js';

const COMPARATORS = {
  outcomes: (r1: WireTypes.WellformedRun, r2: WireTypes.WellformedRun) => {
    if (r1.testStatsOutcomes.regressed !== r2.testStatsOutcomes.regressed)
      return r2.testStatsOutcomes.regressed - r1.testStatsOutcomes.regressed;
    if (r1.testStatsOutcomes.unexpected !== r2.testStatsOutcomes.unexpected)
      return r2.testStatsOutcomes.unexpected - r1.testStatsOutcomes.unexpected;
    if (r1.testStatsOutcomes.flaked !== r2.testStatsOutcomes.flaked)
      return r2.testStatsOutcomes.flaked - r1.testStatsOutcomes.flaked;
    if (r1.testStatsOutcomes.expected !== r2.testStatsOutcomes.expected)
      return r2.testStatsOutcomes.expected - r1.testStatsOutcomes.expected;
    return r2.testStatsOutcomes.skipped - r1.testStatsOutcomes.skipped;
  },
  id: (r1: WireTypes.WellformedRun, r2: WireTypes.WellformedRun) => r2.runId - r1.runId,
  env: (r1: WireTypes.WellformedRun, r2: WireTypes.WellformedRun) => envName(r1).localeCompare(envName(r2)),
  start: (r1: WireTypes.WellformedRun, r2: WireTypes.WellformedRun) => r1.startTimestamp - r2.startTimestamp,
} as const;

function envName(run: WireTypes.WellformedRun): string {
  return Array.from(new Set(run.environments.map(env => env.name))).sort((a, b) => a < b ? -1 : a > b ? 1 : 0).join(', ');
}

type SORT_AXIS = keyof typeof COMPARATORS;

type GlasspanePointerData = {
  leftPx: number,
  width: string,
  timestamp: number,
}

@customElement('runstats-selector')
export class RunstatsSelector extends LitElement {
  @consume({ context: contexts.project, subscribe: true }) private _project?: ContextType<typeof contexts.project>;
  @consume({ context: contexts.dataSnapshotId, subscribe: true }) private _snapshotId?: ContextType<typeof contexts.dataSnapshotId>;

  @property({ attribute: false }) commitStats?: WireTypes.CommitStats;
  @property({ attribute: false }) split?: EnvGroup;
  @property({ attribute: false }) testId?: Stats.TestId;
  @property({ attribute: false }) runlinker?: Linkifier<WireTypes.WellformedRun>;

  // Callers may pass a custom runlinker (e.g. to preserve a timeline split);
  // everyone else gets plain links to the run page.
  private _runlinker(): Linkifier<WireTypes.WellformedRun>|undefined {
    if (this.runlinker)
      return this.runlinker;
    return this._project ? PageRun.linkifier(this._project, this._snapshotId) : undefined;
  }

  @query('sl-dialog') private _dialog?: SlDialog;
  @query('.glasspane') private _glasspane?: HTMLDivElement;

  @state() private _sorter = new Sorter<WireTypes.WellformedRun, SORT_AXIS>([], COMPARATORS);
  @state() private _sortAxis: SORT_AXIS = 'outcomes';
  @state() private _sortDir: WireTypes.SortDirection = 'asc';

  @state() private _hoverPoint?: GlasspanePointerData;
  @state() private _clickPoint?: GlasspanePointerData;

  private _sorterTask = new Task(this, {
    args: () => [this._project, this._snapshotId, this.commitStats, this.split] as const,
    autoRun: false,
    task: async ([project, snapshotId, commitStats, split], { signal }) => {
      CHECK(project && snapshotId && commitStats);
      const elements = await api.history.listCommitRuns.GET({
        orgSlug: project.org.orgSlug,
        projectSlug: project.projectSlug,
        snapshotId,
        commitId: commitStats.commit.commitId,
        timelineSplit: (split ?? EnvGroup.open()).serialize(),
        testId: this.testId,
      }, { signal });
      signal.throwIfAborted();
      this._sorter = new Sorter(elements, COMPARATORS);
      this._sorter.ensureSorted(this._sortAxis, this._sortDir);
    },
  });

  private _onPointerMove(e: PointerEvent) {
    this._hoverPoint = this._createGlasspanePointerData(e);
  }

  private _createGlasspanePointerData(e: PointerEvent): GlasspanePointerData|undefined {
    const rect = this._glasspane?.getBoundingClientRect();
    if (!rect)
      return undefined;

    const x = e.clientX, y = e.clientY;

    const inside =
      x >= rect.left && x <= rect.right &&
      y >= rect.top  && y <= rect.bottom;

    if (!inside)
      return undefined;

    // coords relative to pane:
    const localX = x - rect.left;
    const runs = this._sorter.all();
    const minTime = this.commitStats?.commit.timestamp ?? 0;
    const maxTime = Math.max(...runs.map(run => run.startTimestamp + run.duration));

    return {
      leftPx: localX,
      width: rect.width + 'px',
      timestamp: Math.round(minTime + (maxTime - minTime) * localX / rect.width),
    };
  }

  private _onPointerDown(e: PointerEvent) {
    if ((e.target as HTMLElement).closest('a'))
      return;
    this._clickPoint = this._createGlasspanePointerData(e);
    if (this._clickPoint)
      this._glasspane?.setPointerCapture(e.pointerId);
  }

  private _onPointerUp(e: PointerEvent) {
    if (this._clickPoint)
      this._glasspane?.releasePointerCapture(e.pointerId);
    this._clickPoint = undefined;
  }

  private _onPointerLeave(e: PointerEvent) {
    this._clickPoint = undefined;
    this._hoverPoint = undefined;
  }

  private _clickHandler() {
    this._sorterTask.run();
    this._dialog?.show();
  }

  private _sort(axis: SORT_AXIS) {
    if (axis === this._sortAxis) {
      this._sortDir = this._sortDir === 'asc' ? 'desc' : 'asc';
    } else {
      this._sortAxis = axis;
      this._sortDir = 'asc';
    }
    this._sorter.ensureSorted(this._sortAxis, this._sortDir);
  }

  private _renderColumnHeader(name: TemplateResult<1>|string, axis: SORT_AXIS) {
    const iconName = axis === this._sortAxis && this._sortDir === 'asc' ? 'arrow-down-short' :
      axis === this._sortAxis && this._sortDir === 'desc' ? 'arrow-up-short' :
      nothing;
    return html`
      <button class=column-sort @click=${this._sort.bind(this, axis)}>
        <span>${name}</span>
        <sl-icon name=${iconName}></sl-icon>
      </button>
    `
  }

  render() {
    const runsCount = this.commitStats?.runsCount ?? 0;

    return html`
      <sl-dialog @sl-after-hide=${(event: SlAfterHideEvent) => {
        // Reset sorter to empty to remove heavy-weight dialog content.
        if (event.target === this._dialog) {
          this._sorterTask.abort();
          this._sorter = new Sorter([], COMPARATORS);
        }
      }}>
        <h-box slot=label>
          <span>${runsCount ?? 0} Run${runsCount === 1 ? '' : 's'}</span>
        </h-box>
        ${this._sorterTask.render({
          complete: () => this._renderRunsTimeline(),
          pending: () => html`
            <h1 style="color: var(--fk-color-border);">
              <h-box>
                <sl-spinner></sl-spinner>
                <span>Loading...</span>
              </h-box>
            </h1>
          `
        })}
      </sl-dialog>
      <button class=runs-button @click=${this._clickHandler}>
        <sl-icon name=play-circle></sl-icon>
        <span>${runsCount} ${runsCount === 1 ? 'Run' : 'Runs'}</span>
      </button>
    `;
  }

  private _renderRunsTimeline() {
    const runs = this._sorter.all();
    if (!runs.length)
      return html`<h1 class=no-data-message>No Runs</h1>`;

    const minTime = this.commitStats?.commit.timestamp ?? 0;
    const maxTime = Math.max(...runs.map(run => run.startTimestamp + run.duration));

    const hoverPoint = this._clickPoint ?? this._hoverPoint;

    const hairpinLeft = this._hoverPoint && this._clickPoint ? Math.min(this._hoverPoint.leftPx, this._clickPoint.leftPx) : this._hoverPoint?.leftPx;
    const hairpinWidth = this._hoverPoint && this._clickPoint ? Math.abs(this._hoverPoint.leftPx - this._clickPoint.leftPx) : 0;

    const commitUrl = this.commitStats?.commit && this._project ? PageCommit.url({
      orgSlug: this._project.org.orgSlug,
      projectSlug: this._project.projectSlug,
      sha: this.commitStats?.commit.commitId,
      snapshotId: this._snapshotId,
    }) : nothing;

    return html`
      <section
        @pointermove=${this._onPointerMove}
        @pointerdown=${this._onPointerDown}
        @pointerup=${this._onPointerUp}
        @pointerleave=${this._onPointerLeave}
      >
        <div class=rowheader>
          ${this._renderColumnHeader('Test Results', 'outcomes')}
          ${this._renderColumnHeader('Run #', 'id')}
          ${this._renderColumnHeader('Environment', 'env')}

          <h-box class=timeline-boundary style=${styleMap({
            'justify-content': 'space-between',
            'grid-column': '4',
            'grid-row': '1',
          })}>
            <h-box>
              ${hoverPoint ? html`
                <sl-icon name=geo></sl-icon>
                <h-box style="gap: var(--sl-spacing-2x-small);">
                  ${renderDate(hoverPoint.timestamp)}
                  <sl-icon name=dot></sl-icon>
                  <span>
                    +<time-interval .ms=${hoverPoint.timestamp - minTime}></time-interval>
                  </span>
                </h-box>
              ` : html`
                <sl-icon name=align-start></sl-icon>
                <h-box style="gap: var(--sl-spacing-2x-small);">
                  ${renderDate(minTime)}
                </h-box>
              `}
            </h-box>
            <h-box style="gap: var(--sl-spacing-2x-small)">
              ${this._clickPoint && this._hoverPoint ? html`
                <time-interval .ms=${Math.abs(this._clickPoint.timestamp - this._hoverPoint.timestamp)}></time-interval>
                <sl-icon name=aspect-ratio></sl-icon>
              ` : html`
                <sl-icon name=clock-history></sl-icon>
                <time-interval .ms=${maxTime - minTime}></time-interval>
              `}
            </h-box>
          </h-box>

          <h-box style=${styleMap({
            'justify-content': 'center',
            'grid-column': '4',
            'grid-row': '1',
            'pointer-events': 'none',
          })}>
            ${this._renderColumnHeader(`Timeline`, 'start')}
          </h-box>

        </div>
        ${runs.map((run, idx) => html`
          <div class=row style=${styleMap({
            'grid-row': idx + 2, // explicitly set rows so that glasspane span works.
          })}>
            <outcomes-bar .counts=${run.testStatsOutcomes}></outcomes-bar>
            
            <a class=run-id ${hrefOrClick(this._runlinker()?.(run))}>
              <span>#${run.runId}</span>
              ${run.title ? html`
                <sl-icon name=dot></sl-icon>
                <span class=run-title>${run.title}</span>
              ` : nothing}
            </a>

            <fk-runenvs
              .run=${run}
              .runlinker=${this._runlinker()}
            ></fk-runenvs>

            ${this._renderTimeline(run, minTime, maxTime)}
          </div>
        `)}

        <div class=glasspane style=${styleMap({
          'grid-row': `2/${runs.length + 2}`,
          'grid-column': `4`,
          cursor: this._clickPoint ? 'ew-resize' : 'unset',
        })}>
          ${this._hoverPoint ? html`
            <div class=hovermarker style=${styleMap({
              left: hairpinLeft + 'px',
              width: hairpinWidth + 'px',
              'border-right-width': hairpinWidth > 0 ? '1px' : '0px',
            })}></div>
          ` : nothing}
        </div>
      </section>
    `
  }

  private _renderTimeline(run: WireTypes.WellformedRun, minTimeMs: number, maxTimeMs: number) {
    const left = (run.startTimestamp - minTimeMs) / (maxTimeMs - minTimeMs) * 100;
    const width = run.duration / (maxTimeMs - minTimeMs) * 100;
    const acceptableFlakinessRatio = this._project?.acceptableFlakinessRatio ?? 0;
    const outcome = wireOutcomesToOutcome(run.testStatsOutcomes, acceptableFlakinessRatio);

    let isSelected = false;
    if (this._hoverPoint) {
      const hover1 = (this._hoverPoint ?? this._clickPoint).timestamp;
      const hover2 = (this._clickPoint ?? this._hoverPoint).timestamp;
      const r1 = run.startTimestamp;
      const r2 = run.startTimestamp + run.duration;

      isSelected = Math.abs(hover1 - hover2) + Math.abs(r1 - r2) >= Math.max(hover1, hover2, r1, r2) - Math.min(hover1, hover2, r1, r2);
    }

    return html`
      <v-box style="${styleMap({
        gap: 'var(--sl-spacing-2x-small)',
        'align-self': 'center',
      })}">
        <div style=${styleMap({
          width: '100%',
          'font-size': 'var(--sl-font-size-small)',
          'color': 'var(--sl-color-neutral-500)',
        })}>
          <a draggable=false href=${run.url ?? nothing} target=_blank class=label style=${styleMap({
            left: (left + width / 2) + '%',
            display: 'inline-block',
            position: 'relative',
          })}>
            <time-interval icon .ms=${run.duration} style=${styleMap({
              left: '-50%',
              position: 'relative',
            })}></time-interval>
          </a>
        </div>
        <div class=timeline>
          <a draggable=false href=${run.url ?? nothing} target=_blank class=${classMap({
            runspan: true,
            selected: isSelected
          })} style=${styleMap({
            left: left + '%',
            width: width + '%',
            '--color': `var(--fk-color-outcome-${outcome})`,
          })}></a>
        </div>
      </v-box>
    `;
  }

  static styles = [linkStyles, css`
    .no-data-message {
      color: var(--sl-color-neutral-200);
    }

    outcomes-bar {
      place-self: center;
    }

    .ci-lag-message {
      font-size: var(--sl-font-size-small);
      color: var(--sl-color-neutral-500);
    }

    sl-dialog {
      user-select: none;
      --width: 90vw;
      color: var(--fk-color-text);

      &::part(body) {
        padding-top: 0;
      }
    }

    .timeline-boundary {
      font-weight: normal;
      color: var(--sl-color-neutral-500);
      gap: var(--sl-spacing-x-small);
    }

    button.runs-button {
      all: unset;
      display: flex;
      align-items: center;
      gap: var(--sl-spacing-2x-small);
      cursor: pointer;

      &:hover {
        text-decoration: underline;
      }
    }

    button.column-sort {
      all: unset;
      display: flex;
      align-items: center;
      gap: var(--sl-spacing-2x-small);
      cursor: pointer;
      pointer-events: all;

      &:hover {
        text-decoration: underline;
      }
    }

    section {
      display: grid;
      grid-template-columns: auto auto auto minmax(50%, 1fr);
      overflow: hidden;

      .glasspane {
        position: relative;
        pointer-events: none;

        /*
          Carefully align glasspane with the inner timelines.
          This should be synchronized with row's padding, and gap.
        */
        margin-left: calc((var(--sl-spacing-3x-large) / 2));
        margin-right: var(--sl-spacing-medium);

        .hovermarker {
          position: absolute;
          width: 0px;
          border-left: 1px solid var(--sl-color-neutral-300);
          border-right: 0px solid var(--sl-color-neutral-300);
          background-color: color-mix(in oklch, transparent 80%, var(--sl-color-neutral-200));
          top: 0;
          bottom: 0;
        }
      }

      .rowheader {
        grid-row: 1;
        display: grid;
        grid-column: 1/-1;
        grid-template-columns: subgrid;
        padding: var(--sl-spacing-small) var(--sl-spacing-medium);
        gap: var(--sl-spacing-3x-large);
        border-bottom: 1px solid var(--sl-color-neutral-400);
        font-size: var(--sl-font-size-small);
        font-weight: var(--sl-font-weight-semibold);

        position: sticky;
        top: 0;
        background-color: white;
        z-index: 100;
      }

      .row {
        display: grid;
        grid-column: 1/-1;
        grid-template-columns: subgrid;
        padding: var(--sl-spacing-small) var(--sl-spacing-medium);
        gap: var(--sl-spacing-3x-large);

        &:hover {
          background-color: var(--fk-color-tablerow-hover);
        }
      }

      .row + .row {
        border-top: 1px solid var(--fk-color-border);
      }
    }

    .run-id {
      display: flex;
      align-items: center;
      gap: var(--sl-spacing-2x-small);

      .run-title {
        max-width: 30ch;
      }
    }

    .timeline {
      align-self: center;
      width: 100%;
      position: relative;
      background: #e3f2fd;
      border-radius: var(--sl-border-radius-large);

      .runspan {
        display: block;
        position: relative;
        height: 10px;
        border-radius: var(--sl-border-radius-medium);
        background-color: #2196F3;

        &.selected {
        }
      }
    }
      
    .sha {
      display: inline-flex;
      gap: var(--sl-spacing-2x-small);
      align-items: center;
      font-family: var(--sl-font-mono);
      background: var(--sl-color-amber-50);
      padding: 0 var(--sl-spacing-2x-small);
    }
  `];
}
